// Grids & Gantts — a work item's own screen, as a pop-up.
//
// The client asked for the screen to open "as a pop-up" rather than in a new
// tab, so this is the product's IssueScreenPanel — the same screen Folders
// and the Kanban board open, every field editable — over the page the person
// was already reading, so they keep their place.
//
// It renders into document.body through a portal: the cards on these pages
// clip their overflow, and a dialog that a card can clip is not a dialog.
// Escape closes it, so does the backdrop and the ×.
//
// TWO THINGS A PORTAL GETS WRONG, and both bit (2026-10-01):
//
//  - IT OUTLIVES ITS TAB. Every shell tab stays mounted, so a pop-up on
//    <body> covered the tab the reader went to — View Workflow opened its
//    tab underneath the work item. While its tab is behind, the pop-up is
//    hidden (not closed: it is still there when they come back).
//  - THE SCREEN'S OWN DIALOGS OPENED UNDER IT. The create screen behind the
//    "+" and the transition screens sit at the product's 1000-layer; this
//    is at 4000. The shared create-screen stylesheet lifts them over it
//    while it is open — see ./createScreenCss.js.
//
// ⤢ fills the window (nearly edge to edge) for a work item with a lot on its
// screen — a long checklist, a wide table; ⤡ brings it back. The choice is
// remembered in this browser, so the next work item opens the same way.

import { Component, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IssueScreenPanel from '@modules/database/work-items/frontend/components/IssueScreenPanel';
import { useIsPaneActive } from '@platform/app-shell/frontend/contexts/TabContext';
import { CREATE_SCREEN_CSS } from './createScreenCss';
import { FONT } from '../structure/theme';

class PanelBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) return <div style={{ padding: 16, fontSize: 13, color: '#dc2626' }}>The work-item screen failed to render: {String((this.state.err && this.state.err.message) || this.state.err)}</div>;
    return this.props.children;
  }
}

// Zebra Search opens its work items here too, and hands over what its own
// panel carried: the project's creation tree (the "+ Create" child button),
// the comment a mention mail points at, and the insights its columns use.
// Unset, each is exactly what Grids & Gantts has always passed.
const MAX_KEY = 'stride.workItemPopup.max';
const readMax = () => { try { return localStorage.getItem(MAX_KEY) === '1'; } catch { return false; } };

export default function WorkItemPopup({
  issue, onClose, ui, narrow, title, onOpenTab, onChanged,
  jiraFields = null, fieldNameMap = null,
  creationTree = null, focusCommentId = null, insights = undefined,
}) {
  const inFront = useIsPaneActive();
  const [max, setMax] = useState(readMax);
  // A key clicked inside the work item (its parent, a subtask, a link) opens
  // HERE, not in Zebra Search behind the pop-up; ← Back steps out again.
  // A new item from the page starts a fresh trail.
  const [trail, setTrail] = useState([]);
  useEffect(() => { setTrail([]); }, [issue?.key]);
  const toggleMax = () => setMax((m) => { try { localStorage.setItem(MAX_KEY, m ? '0' : '1'); } catch { /* private mode */ } return !m; });
  const big = max && !narrow;
  useEffect(() => {
    if (!issue || !inFront) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // The page behind a dialog must not scroll under it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [issue, onClose, inFront]);

  if (!issue || typeof document === 'undefined') return null;
  const stepped = trail.length > 0;
  const key = stepped ? trail[trail.length - 1] : issue.key;
  const shownIssue = stepped ? { key } : issue;
  const backTo = stepped ? (trail.length > 1 ? trail[trail.length - 2] : issue.key) : null;

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={`${key} — work item`} aria-hidden={!inFront}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(9, 15, 26, 0.55)',
        display: inFront ? 'flex' : 'none', alignItems: narrow ? 'flex-end' : 'center', justifyContent: 'center',
        padding: narrow ? 0 : (big ? 12 : 24), boxSizing: 'border-box', fontFamily: FONT,
      }}>
      {inFront ? <style>{CREATE_SCREEN_CSS}</style> : null}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: ui.card, color: ui.fg, border: `1px solid ${ui.border}`,
          borderRadius: narrow ? '14px 14px 0 0' : 14, boxShadow: '0 24px 64px rgba(0,0,0,.35)',
          // Wider than it was (1100px): a work item's screen has a side panel
          // and line-item tables, and they were cramped beside each other.
          width: narrow ? '100%' : (big ? '100%' : 'min(1440px, 100%)'), maxHeight: narrow ? '92vh' : (big ? '100%' : '90vh'),
          height: big ? '100%' : undefined,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${ui.border}`, flexWrap: 'wrap' }}>
          {stepped ? (
            <button onClick={() => setTrail((t) => t.slice(0, -1))} title={`Back to ${backTo}`}
              style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${ui.border}`, background: ui.card, color: ui.muted, fontFamily: FONT }}>← {backTo}</button>
          ) : null}
          <strong style={{ fontSize: 13.5 }}>{key}</strong>
          {title && !stepped ? <span style={{ fontSize: 12.5, color: ui.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: narrow ? 180 : 620 }}>{title}</span> : null}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {onOpenTab ? (
              <button onClick={() => onOpenTab(key)} title="Open this work item in its own tab"
                style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${ui.border}`, background: ui.card, color: ui.muted, fontFamily: FONT }}>↗ Open in a tab</button>
            ) : null}
            {!narrow ? (
              <button onClick={toggleMax} aria-label={big ? 'Back to the normal size' : 'Fill the window'} title={big ? 'Back to the normal size' : 'Fill the window'} aria-pressed={big}
                style={{ background: 'none', border: 'none', color: ui.muted, fontSize: 17, lineHeight: 1, cursor: 'pointer', padding: '0 4px', fontFamily: FONT }}>{big ? '⤡' : '⤢'}</button>
            ) : null}
            <button onClick={onClose} aria-label="Close" title="Close (Esc)"
              style={{ background: 'none', border: 'none', color: ui.muted, fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 4px', fontFamily: FONT }}>×</button>
          </span>
        </div>
        <div style={{ overflowY: 'auto', padding: narrow ? '0 10px 12px' : '0 16px 14px', flex: '1 1 auto' }}>
          <PanelBoundary>
            <IssueScreenPanel
              key={key} issueKey={key} issueData={shownIssue}
              onOpenIssue={(k) => setTrail((t) => [...t, k])}
              jiraFields={jiraFields || undefined} fieldNameMap={fieldNameMap || undefined}
              creationTree={creationTree} siteUrl={null}
              focusCommentId={focusCommentId}
              insights={insights}
              onFieldUpdate={(k, fields) => onChanged && onChanged(k, fields)}
              onParentChange={(k, parent) => onChanged && onChanged(k, { parent: parent ? { key: parent } : null })}
              /* `fields` is null when the screen changed the SHAPE of the
                 tree rather than a value; the third argument says how, so a
                 page can take one row out rather than reload everything. */
              onIssueCreated={() => onChanged && onChanged(key, null, 'created')}
              onIssueDeleted={() => { if (onChanged) onChanged(key, null, 'deleted'); if (stepped) setTrail((t) => t.slice(0, -1)); else onClose(); }}
              onClose={onClose} />
          </PanelBoundary>
        </div>
      </div>
    </div>,
    document.body,
  );
}
